import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Clock, ShieldCheck, MapPin, PhoneCall, CreditCard, Baby, ChevronDown, Star, Plane, Car, Users, Crown, Bus } from 'lucide-react';
import { LANDINGS } from './landingData';

const VEHICLE_ICON = { Sedan: Car, Van: Users, VIP: Crown, Minibus: Bus };

// Egypt-specific vehicle price overrides (EUR) so hero card doesn't
// show Swiss numbers on Egypt pages.
const EG_VEHICLE_OVERRIDES = {
  Sedan: 15,
  Van: 20,
  VIP: 35,
  Minibus: 45,
};

function updateMeta(name, content, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function updateCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

function upsertJsonLd(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

export default function AirportTransferLanding({ landingKey }) {
  const navigate = useNavigate();
  const location = useLocation();
  const data = LANDINGS[landingKey];
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (!data) return;
    document.title = data.title;
    updateMeta('description', data.metaDescription);
    updateMeta('og:title', data.title, true);
    updateMeta('og:description', data.metaDescription, true);
    updateMeta('og:type', 'website', true);
    updateMeta('og:url', `https://366transfer.com${data.slug}`, true);
    updateCanonical(`https://366transfer.com${data.slug}`);
    if (data.lang) document.documentElement.lang = data.lang;

    // TaxiService schema
    upsertJsonLd('schema-taxiservice', {
      '@context': 'https://schema.org',
      '@type': 'TaxiService',
      name: data.airport ? `${data.airport.long} Transfer` : '366Transfer Airport Transfer',
      url: `https://366transfer.com${data.slug}`,
      areaServed: data.airport ? { '@type': 'Place', name: data.airport.region } : { '@type': 'Country', name: data.country || 'Switzerland' },
      priceRange: data.priceRange || 'CHF 75 – CHF 650',
      provider: { '@type': 'Organization', name: '366Transfer.com GmbH', url: 'https://366transfer.com' },
    });
    // FAQPage schema
    upsertJsonLd('schema-faqpage', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: data.faqs.map(f => ({
        '@type': 'Question', name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    // BreadcrumbList schema
    const breadcrumbs = [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `https://366transfer.com/${data.lang === 'en' ? 'en' : ''}` },
      { '@type': 'ListItem', position: 2, name: data.lang === 'en' ? 'Airport Transfer' : 'Flughafentransfer', item: `https://366transfer.com${data.lang === 'en' ? '/en/airport-transfer' : '/flughafentransfer'}` },
    ];
    if (data.airport) breadcrumbs.push({ '@type': 'ListItem', position: 3, name: data.airport.name, item: `https://366transfer.com${data.slug}` });
    upsertJsonLd('schema-breadcrumb', { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: breadcrumbs });

    return () => {
      ['schema-taxiservice', 'schema-faqpage', 'schema-breadcrumb'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [data]);

  if (!data) return <div className="p-12 text-center text-slate-400">Not found</div>;
  const isEn = data.lang === 'en';
  const t = (de, en) => isEn ? en : de;

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`landing-${landingKey}`}>
      {/* Breadcrumb */}
      <nav className="max-w-6xl mx-auto px-4 pt-4 text-xs text-slate-500" aria-label="Breadcrumb">
        <Link to={isEn ? '/en/airport-transfer' : '/'} className="hover:text-orange-500">{t('Startseite', 'Home')}</Link>
        <span className="mx-2">/</span>
        <Link to={isEn ? '/en/airport-transfer' : '/flughafentransfer'} className="hover:text-orange-500">{t('Flughafentransfer', 'Airport Transfer')}</Link>
        {data.airport && <>
          <span className="mx-2">/</span>
          <span className="text-slate-700">{data.airport.name}</span>
        </>}
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-8 pb-12">
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
              <Plane size={12} /> {data.airport ? data.airport.long : t('Flughafentransfer in der ganzen Schweiz', 'Airport transfers all over Switzerland')}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-4" data-testid="landing-h1">
              {data.h1}
            </h1>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">{data.intro}</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/booking')} className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold text-sm shadow-md" data-testid="landing-cta-primary">
                {t('Jetzt Transfer buchen →', 'Book transfer now →')}
              </button>
              <a href="tel:+41800000000" className="px-6 py-3 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg font-medium text-sm text-slate-700 inline-flex items-center gap-2">
                <PhoneCall size={14} /> {t('24/7 Support', '24/7 Support')}
              </a>
            </div>
          </div>
          {/* Price card */}
          <aside className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <p className="text-xs font-semibold uppercase text-slate-400 mb-2">{t('Beispielpreise', 'Sample prices')}</p>
            <div className="space-y-3">
              {data.vehicles[data.lang].map(v => {
                const Icon = VEHICLE_ICON[v.type] || Car;
                const price = data.country === 'Egypt' ? (EG_VEHICLE_OVERRIDES[v.type] ?? v.priceFrom) : v.priceFrom;
                return (
                  <div key={v.type} className="flex items-center gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center"><Icon size={18} className="text-slate-600" /></div>
                    <div className="flex-1">
                      <p className="font-bold text-slate-800 text-sm">{v.type} <span className="font-normal text-slate-400 text-xs">· {v.example}</span></p>
                      <p className="text-xs text-slate-500">{v.capacity}</p>
                    </div>
                    <p className="font-extrabold text-orange-600">{data.currency || 'CHF'} {price}</p>
                  </div>
                );
              })}
            </div>
            <button onClick={() => navigate('/booking')} className="w-full mt-4 px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold">
              {t('Angebote erhalten →', 'Get offers →')}
            </button>
          </aside>
        </div>
      </section>

      {/* Body content (SEO) */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-10">
          <p className="text-slate-700 leading-relaxed text-base">{data.body}</p>
        </div>
      </section>

      {/* USPs */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">
          {t('Warum 366Transfer wählen?', 'Why choose 366Transfer?')}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.usps[data.lang].map(u => (
            <div key={u.title} className="bg-white rounded-xl border border-slate-100 p-5">
              <div className="w-10 h-10 bg-orange-50 text-orange-600 rounded-lg flex items-center justify-center mb-3">
                <ShieldCheck size={20} />
              </div>
              <h3 className="font-bold text-slate-800 mb-1.5">{u.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{u.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Airports (only on main pages) */}
      {data.airports && (
        <section className="bg-slate-800 py-14 text-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center">
              {t('Unsere Flughäfen', 'Our airports')}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.airports.map(a => (
                <Link key={a.slug} to={a.slug} className="bg-slate-700 hover:bg-slate-600 rounded-xl p-5 transition-colors">
                  <Plane size={20} className="text-orange-400 mb-2" />
                  <h3 className="font-bold mb-1">{a.name}</h3>
                  <p className="text-xs text-slate-300">{a.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Popular routes */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-center">
          {t('Beliebte Strecken', 'Popular routes')}
        </h2>
        <p className="text-slate-500 text-center mb-8 text-sm">{t('Fixpreise – kein Nachtzuschlag, keine versteckten Gebühren.', 'Fixed prices – no night surcharges, no hidden fees.')}</p>
        <div className="grid md:grid-cols-2 gap-3">
          {data.routes.map((r, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4">
              <div className="w-9 h-9 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center shrink-0">
                <MapPin size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 text-sm truncate">{r.from} → {r.to}</p>
                {r.distance && <p className="text-xs text-slate-400">{r.distance} · {r.time}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className="font-extrabold text-slate-900">{data.currency || 'CHF'} {r.price}</p>
                <button onClick={() => navigate('/booking')} className="text-xs text-orange-500 hover:underline">{t('Buchen →', 'Book →')}</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Price table (optional – e.g. taxi hurghada preise).
         Uses a div-grid instead of <table> to avoid HTML nesting violations
         when third-party dev tooling wraps rows in <span>. */}
      {data.priceTable && (
        <section className="bg-white border-y border-slate-100 py-14" data-testid="price-table-section">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 text-center">
              {t('Transparente Preise – keine Verhandlung nötig', 'Transparent prices – no haggling')}
            </h2>
            <p className="text-slate-500 text-center mb-8 text-sm">
              {t('Alle Preise sind Fixpreise pro Fahrt (nicht pro Person). Inklusive Steuern und Wartezeit.', 'All prices are per ride (not per person). Includes taxes and waiting time.')}
            </p>
            <div className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
              <div className="grid grid-cols-4 gap-2 px-4 py-3 bg-slate-100 text-xs font-semibold text-slate-500 uppercase">
                <div>{t('Strecke', 'Route')}</div>
                <div className="text-right">Sedan</div>
                <div className="text-right">Van</div>
                <div className="text-right">VIP</div>
              </div>
              {data.priceTable.map((row, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 px-4 py-3 border-t border-slate-200 hover:bg-white text-sm" data-testid={`price-row-${i}`}>
                  <div className="font-medium text-slate-700 min-w-0">{row.route}</div>
                  <div className="text-right text-slate-800 font-bold">{data.currency || '€'} {row.sedan}</div>
                  <div className="text-right text-slate-800 font-bold">{data.currency || '€'} {row.van}</div>
                  <div className="text-right text-slate-800 font-bold">{data.currency || '€'} {row.vip}</div>
                </div>
              ))}
            </div>
            <div className="text-center mt-6">
              <button onClick={() => navigate('/booking')} className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold shadow-md" data-testid="price-table-cta">
                {t('Jetzt zum Fixpreis buchen →', 'Book at fixed price →')}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Map embed (optional) */}
      {data.mapEmbedUrl && (
        <section className="max-w-6xl mx-auto px-4 py-14" data-testid="map-section">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">
            {t('Häufige Ziele auf der Karte', 'Popular destinations on the map')}
          </h2>
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
            <iframe
              src={data.mapEmbedUrl}
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={data.airport?.name || 'Route map'}
            />
          </div>
        </section>
      )}

      {/* Steps */}
      <section className="bg-white border-y border-slate-100 py-14">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-10 text-center">
            {t('So einfach geht\'s', 'How it works')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {data.steps[data.lang].map(s => (
              <div key={s.n} className="text-center">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">{s.n}</div>
                <h3 className="font-bold text-slate-800 mb-2">{s.t}</h3>
                <p className="text-sm text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 py-14">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">
          {t('Häufig gestellte Fragen', 'Frequently asked questions')}
        </h2>
        <div className="space-y-2">
          {data.faqs.map((f, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-slate-50">
                <span className="font-medium text-slate-800 pr-3">{f.q}</span>
                <ChevronDown size={16} className={`text-slate-400 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && (
                <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Reviews (static example) */}
      <section className="bg-white border-y border-slate-100 py-14">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-1 mb-2">
              {[...Array(5)].map((_, i) => <Star key={i} size={18} className="text-amber-400 fill-amber-400" />)}
              <span className="ml-2 font-bold text-slate-800">4.8/5</span>
              <span className="text-sm text-slate-500">· 312 {t('Bewertungen', 'reviews')}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('Was unsere Kunden sagen', 'What our customers say')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: 'S. Müller', text: t('Perfekt organisiert. Fahrer war pünktlich, Auto sauber, Preis wie vereinbart.', 'Perfectly organized. Driver on time, clean car, price as agreed.') },
              { name: 'A. Schneider', text: t('Nach 12 Stunden Flug war es Gold wert, dass jemand mit meinem Namen wartete.', 'After a 12-hour flight it was worth gold to have someone waiting with my name.') },
              { name: 'M. Rossi', text: t('Buche seit 2 Jahren regelmässig für Business-Reisen. Immer top.', 'Been booking regularly for business trips for 2 years. Always top.') },
            ].map((r, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-5">
                <div className="flex gap-0.5 mb-2">
                  {[...Array(5)].map((_, j) => <Star key={j} size={12} className="text-amber-400 fill-amber-400" />)}
                </div>
                <p className="text-sm text-slate-700 mb-3 leading-relaxed">&ldquo;{r.text}&rdquo;</p>
                <p className="text-xs font-medium text-slate-500">— {r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            {t(`Bereit für Ihren ${data.airport ? data.airport.name + ' ' : ''}Flughafentransfer?`, `Ready for your ${data.airport ? data.airport.name + ' ' : ''}airport transfer?`)}
          </h2>
          <p className="mb-6 text-orange-50">
            {t('In 60 Sekunden Angebote vergleichen und sicher online buchen.', 'Compare offers in 60 seconds and book securely online.')}
          </p>
          <button onClick={() => navigate('/booking')} className="px-8 py-3 bg-white text-orange-600 rounded-lg font-bold shadow-lg hover:shadow-xl transition-shadow" data-testid="landing-cta-bottom">
            {t('Jetzt Transfer buchen →', 'Book transfer now →')}
          </button>
        </div>
      </section>

      {/* Footer navigation to related landings */}
      {!data.airport && (
        <section className="bg-slate-900 py-10 text-white">
          <div className="max-w-6xl mx-auto px-4">
            <p className="text-xs uppercase text-slate-500 mb-3">{t('Andere Sprachen', 'Other languages')}</p>
            <div className="flex flex-wrap gap-3">
              <Link to={isEn ? '/flughafentransfer' : '/en/airport-transfer'} className="text-orange-400 hover:underline text-sm">
                {isEn ? '🇩🇪 Deutsche Version' : '🇬🇧 English version'}
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
